export interface ErrorWithRetry extends Error {
  canRetry?: boolean;
  retryAfter?: number;
  code?: string;
}

export interface RetryOptions {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
}

export const defaultRetryOptions: RetryOptions = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffFactor: 2
};

export class RetryableError extends Error implements ErrorWithRetry {
  canRetry: boolean;
  retryAfter?: number;
  code?: string;

  constructor(message: string, canRetry = true, code?: string, retryAfter?: number) {
    super(message);
    this.name = 'RetryableError';
    this.canRetry = canRetry;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

export class ApiError extends Error {
  statusCode?: number;
  code?: string;
  context?: any;
  isNetworkError: boolean;
  isValidationError: boolean;
  validationErrors?: Record<string, string[]>;

  constructor(
    message: string,
    statusCode?: number,
    code?: string,
    context?: any,
    validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.context = context;
    this.isNetworkError = isNetworkError(this);
    this.isValidationError = !!validationErrors && Object.keys(validationErrors).length > 0;
    this.validationErrors = validationErrors;
  }

  isAuthError(): boolean {
    return this.statusCode === 401 || this.statusCode === 403;
  }

  getUserMessage(): string {
    return getUserFriendlyErrorMessage(this);
  }

  getValidationErrors(): Record<string, string[]> {
    return this.validationErrors || {};
  }
}

export class AppError extends Error {
  code?: string;
  context?: any;

  constructor(message: string, code?: string, context?: any) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.context = context;
  }
}

export const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

export const withRetry = async <T>(
  operation: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> => {
  const config = { ...defaultRetryOptions, ...options };
  let lastError: Error = new Error('Unknown error');

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      // Don't retry if it's not a retryable error
      if (error instanceof RetryableError && !error.canRetry) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === config.maxAttempts) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        config.baseDelay * Math.pow(config.backoffFactor, attempt - 1),
        config.maxDelay
      );

      // Use custom retry delay if provided
      const retryDelay = (error as ErrorWithRetry).retryAfter || delay;

      console.warn(`Operation failed (attempt ${attempt}/${config.maxAttempts}), retrying in ${retryDelay}ms:`, error);

      await sleep(retryDelay);
    }
  }

  // lastError is guaranteed to be assigned since we only reach here after at least one failed attempt
  throw lastError!;
};

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
};

export const getErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as any).code;
  }

  return undefined;
};

export const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const networkErrorMessages = [
    'network error',
    'fetch failed',
    'failed to fetch',
    'network request failed',
    'connection refused',
    'timeout'
  ];

  return networkErrorMessages.some(msg =>
    error.message.toLowerCase().includes(msg)
  );
};

export const isWalletError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const walletErrorMessages = [
    'user rejected',
    'user denied',
    'metamask',
    'wallet not found',
    'no ethereum provider',
    'wallet connection failed'
  ];

  return walletErrorMessages.some(msg =>
    error.message.toLowerCase().includes(msg)
  );
};

export const getUserFriendlyErrorMessage = (error: unknown): string => {
  const message = getErrorMessage(error);
  const lowerMessage = message.toLowerCase();

  // Wallet-specific errors
  if (lowerMessage.includes('user rejected') || lowerMessage.includes('user denied')) {
    return 'Transaction was cancelled by user';
  }

  if (lowerMessage.includes('metamask') || lowerMessage.includes('no ethereum provider')) {
    return 'MetaMask is not installed or not available';
  }

  if (lowerMessage.includes('wallet not found')) {
    return 'No wallet found. Please install MetaMask or another supported wallet';
  }

  // Network errors
  if (isNetworkError(error)) {
    return 'Network connection error. Please check your internet connection and try again';
  }

  // API errors
  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
    return 'Authentication required. Please log in again';
  }

  if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
    return 'Access denied. You do not have permission to perform this action';
  }

  if (lowerMessage.includes('not found') || lowerMessage.includes('404')) {
    return 'Requested resource not found';
  }

  if (lowerMessage.includes('server error') || lowerMessage.includes('500')) {
    return 'Server error. Please try again later';
  }

  if (lowerMessage.includes('timeout')) {
    return 'Request timed out. Please try again';
  }

  // Rate limiting
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('429')) {
    return 'Too many requests. Please wait a moment and try again';
  }

  // Default fallback
  return message || 'An unexpected error occurred';
};

export const logError = (error: unknown, context?: string) => {
  const errorInfo = {
    message: getErrorMessage(error),
    code: getErrorCode(error),
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  console.error('Error logged:', errorInfo);

  // In production, send to error monitoring service
  if (process.env.NODE_ENV === 'production') {
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }
};